import { Injectable } from "@angular/core";
import {
    ITCIConnection,
    ITCIConnectionServiceProvider,
    ITCIConnector,
    TCI_COMPONENT_MODE,
    TCI_CONNECTIONS_MENU_TYPE,
    TCITenantAppType,
    TCITenantConnectionsMenu
} from "@tci/common";
import { TciTenantModelService, ITCITenantServices } from "../ng-tenant/tci-tenant.model";
import { TCITenantMenuServices } from "../ng-tenant/tci-tenant.menu";
import { BaseConnectionMenuComponentInput } from "./ng-connections-menu";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { NgAppFeatureToggleService } from "../ng-shared/ng-services/ng-app-feature-toggle.service";

import { BehaviorSubject, Observable } from "rxjs";
import * as lodash from "lodash";
import * as CryptoJS from "crypto-js";

export interface IConnectionMenuOpts {
    connection: ITCIConnection;
    comps: TCITenantConnectionsMenu[];
}

const CONNS_SESSION_ITEM = "TCI-CONNS-";

@Injectable()
export class NgConnectionsService {
    private static instance: NgConnectionsService = null;

    // Status Menu artifacts
    statusMenuOptions$: BehaviorSubject<IConnectionMenuOpts[]> = new BehaviorSubject([]);
    private storeKey: string;

    public static getInstance(
        tenantRemoteServices: TCITenantRemoteServices,
        tenantSvc: TciTenantService,
        tenantMenuSrv: TCITenantMenuServices,
        tenantModelSvc: TciTenantModelService,
        userService: NgUserService,
        featureToggleService: NgAppFeatureToggleService
    ): NgConnectionsService {
        if (!NgConnectionsService.instance) {
            NgConnectionsService.instance = new NgConnectionsService(
                tenantRemoteServices,
                tenantSvc,
                tenantMenuSrv,
                tenantModelSvc,
                userService,
                featureToggleService);
        }
        return NgConnectionsService.instance;
    }

    constructor(
        private tenantRemoteSvc: TCITenantRemoteServices,
        private tenantSvc: TciTenantService,
        private tenantMenuSrv: TCITenantMenuServices,
        private tenantModelSvc: TciTenantModelService,
        private userSvc: NgUserService,
        private featureToggleSvc: NgAppFeatureToggleService
    ) {
    }

    public getConnectors(): Observable<ITCIConnector[]> {
        return this.getProviders()
            .flatMap(providers => {
                const tenantsConnectorsObs = providers.map(provider => provider.svc
                    .getConnectors()
                    .catch(e => {
                        console.error(
                            "[Connections/Connectors] Error loading data from tenant",
                            provider.tenant.toString(), e
                        );
                        return [];
                    }));
                return Observable.merge(...tenantsConnectorsObs)
                    .toArray()
                    .map(connsArr => lodash.sortBy(lodash.flatten(connsArr), [conn => conn.name.toLowerCase()]));
            });
    }

    private getStoreKey(): Observable<string> {
        if (this.storeKey) {
            return Observable.of(this.storeKey);
        }

        return this.userSvc.getUserInfo()
            .map(userInfo => {
                this.storeKey = CONNS_SESSION_ITEM + CryptoJS.SHA256(userInfo.userId).toString();
                return this.storeKey;
            });
    }

    public getConnections(isReload = false): Observable<ITCIConnection[]> {
        if (isReload) {
            this.statusMenuOptions$.next([]);
            this.statusMenuOptions$.complete();
            this.statusMenuOptions$ = new BehaviorSubject([]);
        }
        return this.getProviders()
            .flatMap(providers => {
                const tenantsConnectorsObs = providers.map(provider =>
                    provider.svc
                        .getConnections()
                        .catch(e => {
                            console.error(
                                "[Connections] Error loading data from tenant",
                                provider.tenant.toString(), e
                            );
                            return [];
                        }));
                return Observable.merge(...tenantsConnectorsObs)
                    .toArray()
                    .map(connsArr => lodash.flatten(connsArr))
                    .map(connsArr => lodash.sortBy(connsArr, [conn => conn.name.toLowerCase()]))
                    .map(connsArr => {
                        this.setStatusMenuOptions(connsArr);
                        return connsArr;
                    });
            });
    }

    public getConnection(connId: string): Observable<ITCIConnection> {
        return this.getConnections()
            .map(conns => conns.find(conn => conn.id === connId));
    }

    // Status Menu Methods
    private setStatusMenuOptions(connections: ITCIConnection[]): void {
        const obsCollection: Observable<IConnectionMenuOpts>[] = [];
        connections
            .map(connection => {
                const input = new BaseConnectionMenuComponentInput(
                    connection.tenantType,
                    null,
                    connection,
                    this.userSvc,
                    this.featureToggleSvc,
                    TCI_COMPONENT_MODE.COMP_MODE_CONN_HAMBURGER,
                    undefined,
                    null);
                obsCollection.push(
                    this.tenantMenuSrv
                        .getConnectionsMenuItems(
                            connection.tenantType,
                            (v: TCITenantConnectionsMenu) => v.getOptionType() === TCI_CONNECTIONS_MENU_TYPE.HAMBURGER_MENU,
                            input
                        )
                        .map((comps: TCITenantConnectionsMenu[]) => ({ connection: connection, comps: comps || [] }))
                );
            });

        Observable.merge(...obsCollection)
            .toArray()
            .subscribe(res => this.statusMenuOptions$.next(res));
    }

    private getProviders(): Observable<{ tenant: TCITenantAppType, svc: ITCIConnectionServiceProvider }[]> {
        return this.tenantSvc.onInit()
            .flatMap(() => this.tenantRemoteSvc.getRemoteServices())
            .map(services => services.map(service => TCITenantAppType.fromString(service.name)))
            .map(types => types
                .map(tenant => ({ tenant, svc: this.tenantModelSvc.getTenant(tenant) as any }))
                .filter(tenantProvider => !!tenantProvider.svc)
                .map(tenantProvider => {
                    tenantProvider.svc = tenantProvider.svc.getConnectionServiceProvider();
                    return tenantProvider;
                })
                .filter(tenantProvider => !!tenantProvider.svc)
            );
    }
}
